'use client';

import { useEffect, useRef } from 'react';

/**
 * Last resort: the root layout itself failed, so Next replaces it entirely and
 * this component supplies its own `<html>` and `<body>`.
 *
 * Everything here is inline and self-contained on purpose. The stylesheet, the
 * fonts and the component library all hang off the layout that just threw, and
 * a fallback that depends on the thing it is a fallback for is not one. The
 * brand colours are repeated as literals for the same reason — this is the one
 * file where a missing CSS variable would leave the user staring at nothing.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): React.ReactElement {
  const reported = useRef(false);

  useEffect(() => {
    if (reported.current) {
      return;
    }
    reported.current = true;

    void fetch('/api/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        app: 'dashboard',
        route: window.location.pathname,
        message: error.message || 'Unknown error',
        digest: error.digest,
        stack: error.stack,
      }),
      keepalive: true,
    }).catch(() => {
      // Best-effort. A failed report must not become a second failure.
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem 1rem',
          background: '#0d0d0d',
          color: '#fafafa',
          fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
        }}
      >
        <main
          style={{
            maxWidth: '30rem',
            width: '100%',
            textAlign: 'center',
            background: '#1a1a1a',
            border: '1px solid rgba(28, 28, 28, 0.1)',
            borderRadius: '1.5rem',
            padding: '2rem',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.5rem' }}>
            The dashboard could not load
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#a1a1a1', margin: '0 0 1.5rem' }}>
            Something failed before the page could start. Reloading usually clears it.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              border: 0,
              borderRadius: '999px',
              background: '#fafafa',
              color: '#0d0d0d',
              fontSize: '0.875rem',
              fontWeight: 600,
              padding: '0.7rem 1.5rem',
              cursor: 'pointer',
            }}
          >
            Reload the dashboard
          </button>
          {error.digest ? (
            <p style={{ fontSize: '0.75rem', color: '#a1a1a1', margin: '1.5rem 0 0' }}>
              Reference <span style={{ fontFamily: 'ui-monospace, monospace' }}>{error.digest}</span>
            </p>
          ) : null}
        </main>
      </body>
    </html>
  );
}
